import { useState, useCallback } from 'react';
import { Node } from '@xyflow/react';
import {
    ExpansionViewState,
    CustomNodeData,
    ClusterCacheEntry,
    NamespaceCacheEntry,
    AwsAccountCacheEntry,
    OciTenancyCacheEntry,
    INITIAL_EXPANSION_STATE,
} from '../types';
import { InventoryCluster, InventoryNamespace, AWSConnection, OCIConnection, CloudResource } from '../../../api/client';

// ============================================================================
// TYPES
// ============================================================================

export interface UseExpansionStateInput {
    clusters: InventoryCluster[];
    clusterCache: Map<string, ClusterCacheEntry>;
    loadClusterData: (clusterUID: string, cluster: InventoryCluster) => Promise<ClusterCacheEntry>;
    loadNamespaceData: (namespaceID: string, namespace: InventoryNamespace, clusterUID: string) => Promise<NamespaceCacheEntry>;
    // AWS support
    awsConnections?: AWSConnection[];
    awsAccountCache?: Map<string, AwsAccountCacheEntry>;
    loadAwsAccountData?: (accountId: string, connection: AWSConnection) => Promise<AwsAccountCacheEntry>;
    // OCI support
    ociConnections?: OCIConnection[];
    ociTenancyCache?: Map<string, OciTenancyCacheEntry>;
    loadOciTenancyData?: (tenancyOcid: string, connection: OCIConnection) => Promise<OciTenancyCacheEntry>;
    // Cloud resource click callback (for showing detail dialog)
    onCloudResourceClick?: (resource: CloudResource) => void;
}

export interface UseExpansionStateResult {
    viewState: ExpansionViewState;

    // Actions
    toggleClusterExpansion: (clusterUID: string) => Promise<void>;
    toggleNamespaceExpansion: (namespaceID: string, clusterUID: string) => Promise<void>;
    toggleAwsAccountExpansion: (accountId: string) => Promise<void>;
    toggleVpcExpansion: (vpcId: string, accountId: string) => void;
    toggleOciTenancyExpansion: (tenancyOcid: string) => Promise<void>;
    toggleVcnExpansion: (vcnId: string, tenancyOcid: string) => void;
    collapseAll: () => void;
    expandAllClusters: () => Promise<void>;
    handleNodeClick: (event: React.MouseEvent | null, node: Node<CustomNodeData>) => Promise<void>;
    handleBreadcrumbClick: (index: number) => void;

    // State checks
    isClusterExpanded: (clusterUID: string) => boolean;
    isNamespaceExpanded: (namespaceID: string) => boolean;
    isAwsAccountExpanded: (accountId: string) => boolean;
    isVpcExpanded: (vpcId: string) => boolean;
    isOciTenancyExpanded: (tenancyOcid: string) => boolean;
    isVcnExpanded: (vcnId: string) => boolean;

    // Animation state
    animatingNodes: Set<string>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useExpansionState(input: UseExpansionStateInput): UseExpansionStateResult {
    const {
        clusters,
        clusterCache,
        loadClusterData,
        loadNamespaceData,
        awsConnections = [],
        awsAccountCache = new Map(),
        loadAwsAccountData,
        ociConnections = [],
        ociTenancyCache = new Map(),
        loadOciTenancyData,
        onCloudResourceClick,
    } = input;

    // =========================================================================
    // STATE
    // =========================================================================

    const [viewState, setViewState] = useState<ExpansionViewState>(INITIAL_EXPANSION_STATE);
    const [animatingNodes, setAnimatingNodes] = useState<Set<string>>(() => new Set());

    // =========================================================================
    // ANIMATION HELPERS
    // =========================================================================

    const startAnimation = useCallback((nodeId: string) => {
        setAnimatingNodes(prev => {
            const next = new Set(prev);
            next.add(nodeId);
            return next;
        });

        // Auto-cleanup animation state after transition completes
        setTimeout(() => {
            setAnimatingNodes(prev => {
                const next = new Set(prev);
                next.delete(nodeId);
                return next;
            });
        }, 350);
    }, []);

    // =========================================================================
    // STATE CHECKS
    // =========================================================================

    const isClusterExpanded = useCallback((clusterUID: string): boolean => {
        return viewState.expandedClusters.has(clusterUID);
    }, [viewState.expandedClusters]);

    const isNamespaceExpanded = useCallback((namespaceID: string): boolean => {
        return viewState.expandedNamespaces.has(namespaceID);
    }, [viewState.expandedNamespaces]);

    const isAwsAccountExpanded = useCallback((accountId: string): boolean => {
        return viewState.expandedAwsAccounts.has(accountId);
    }, [viewState.expandedAwsAccounts]);

    const isVpcExpanded = useCallback((vpcId: string): boolean => {
        return viewState.expandedVpcs.has(vpcId);
    }, [viewState.expandedVpcs]);

    const isOciTenancyExpanded = useCallback((tenancyOcid: string): boolean => {
        return viewState.expandedOciTenancies.has(tenancyOcid);
    }, [viewState.expandedOciTenancies]);

    const isVcnExpanded = useCallback((vcnId: string): boolean => {
        return viewState.expandedVcns.has(vcnId);
    }, [viewState.expandedVcns]);

    // =========================================================================
    // TOGGLE CLUSTER EXPANSION
    // =========================================================================

    const toggleClusterExpansion = useCallback(async (clusterUID: string) => {
        const cluster = clusters.find(c => c.uid === clusterUID);
        if (!cluster) {
            console.warn(`[Expansion] Cluster not found: ${clusterUID}`);
            return;
        }

        const isCurrentlyExpanded = viewState.expandedClusters.has(clusterUID);

        // Start animation
        startAnimation(clusterUID);

        if (isCurrentlyExpanded) {
            // =====================================================
            // COLLAPSE CLUSTER
            // =====================================================

            setViewState(prev => {
                // Remove cluster from expanded set
                const newExpandedClusters = new Set(prev.expandedClusters);
                newExpandedClusters.delete(clusterUID);

                // Also collapse all namespaces within this cluster
                const clusterData = clusterCache.get(clusterUID);
                const namespaceIDs = clusterData?.namespaces?.map(ns => ns.id) || [];

                const newExpandedNamespaces = new Set(prev.expandedNamespaces);
                namespaceIDs.forEach(id => newExpandedNamespaces.delete(id));

                // Update breadcrumbs - remove this cluster and anything after
                const clusterBreadcrumbIndex = prev.breadcrumbs.findIndex(
                    b => b.type === 'cluster' && b.id === clusterUID
                );
                const newBreadcrumbs = clusterBreadcrumbIndex >= 0
                    ? prev.breadcrumbs.slice(0, clusterBreadcrumbIndex)
                    : prev.breadcrumbs;

                return {
                    ...prev,
                    expandedClusters: newExpandedClusters,
                    expandedNamespaces: newExpandedNamespaces,
                    breadcrumbs: newBreadcrumbs.length > 0
                        ? newBreadcrumbs
                        : [{ label: 'All Clusters', type: 'root' }]
                };
            });
        } else {
            // =====================================================
            // EXPAND CLUSTER
            // =====================================================

            // Load data if not cached
            try {
                await loadClusterData(clusterUID, cluster);
            } catch (err) {
                console.error(`[Expansion] Failed to load cluster data:`, err);
                return; // Don't expand if data load failed
            }

            setViewState(prev => {
                const newExpandedClusters = new Set(prev.expandedClusters);
                newExpandedClusters.add(clusterUID);

                // Add to breadcrumbs if not already there
                const hasClusterBreadcrumb = prev.breadcrumbs.some(
                    b => b.type === 'cluster' && b.id === clusterUID
                );

                const newBreadcrumbs = hasClusterBreadcrumb
                    ? prev.breadcrumbs
                    : [...prev.breadcrumbs, {
                        label: cluster.name,
                        type: 'cluster' as const,
                        id: clusterUID
                    }];

                return {
                    ...prev,
                    expandedClusters: newExpandedClusters,
                    breadcrumbs: newBreadcrumbs,
                    focusedCluster: clusterUID
                };
            });
        }
    }, [clusters, viewState.expandedClusters, clusterCache, loadClusterData, startAnimation]);

    // =========================================================================
    // TOGGLE NAMESPACE EXPANSION
    // =========================================================================

    const toggleNamespaceExpansion = useCallback(async (namespaceID: string, clusterUID: string) => {
        // Find the namespace in the cluster cache
        const clusterData = clusterCache.get(clusterUID);
        const namespace = clusterData?.namespaces?.find(ns => ns.id === namespaceID);

        if (!namespace) {
            console.warn(`[Expansion] Namespace not found: ${namespaceID}`);
            return;
        }

        const isCurrentlyExpanded = viewState.expandedNamespaces.has(namespaceID);

        // Start animation
        startAnimation(namespaceID);

        if (isCurrentlyExpanded) {
            // =====================================================
            // COLLAPSE NAMESPACE
            // =====================================================

            setViewState(prev => {
                const newExpandedNamespaces = new Set(prev.expandedNamespaces);
                newExpandedNamespaces.delete(namespaceID);

                // Don't modify breadcrumbs - namespaces are siblings, not nested
                return {
                    ...prev,
                    expandedNamespaces: newExpandedNamespaces
                };
            });
        } else {
            // =====================================================
            // EXPAND NAMESPACE
            // =====================================================

            // Load data if not cached
            try {
                await loadNamespaceData(namespaceID, namespace, clusterUID);
            } catch (err) {
                console.error(`[Expansion] Failed to load namespace data:`, err);
                return; // Don't expand if data load failed
            }

            setViewState(prev => {
                const newExpandedNamespaces = new Set(prev.expandedNamespaces);
                newExpandedNamespaces.add(namespaceID);

                // Don't modify breadcrumbs - namespaces are siblings, not nested
                return {
                    ...prev,
                    expandedNamespaces: newExpandedNamespaces
                };
            });
        }
    }, [clusterCache, viewState.expandedNamespaces, loadNamespaceData, startAnimation]);

    // =========================================================================
    // TOGGLE AWS ACCOUNT EXPANSION
    // =========================================================================

    const toggleAwsAccountExpansion = useCallback(async (accountId: string) => {
        const connection = awsConnections.find(c => c.account_id === accountId);
        if (!connection) {
            console.warn(`[Expansion] AWS account not found: ${accountId}`);
            return;
        }

        const isCurrentlyExpanded = viewState.expandedAwsAccounts.has(accountId);

        // Start animation
        startAnimation(`aws-account:${accountId}`);

        if (isCurrentlyExpanded) {
            // =====================================================
            // COLLAPSE AWS ACCOUNT
            // =====================================================

            setViewState(prev => {
                // Remove account from expanded set
                const newExpandedAccounts = new Set(prev.expandedAwsAccounts);
                newExpandedAccounts.delete(accountId);

                // Also collapse all VPCs within this account
                const accountData = awsAccountCache.get(accountId);
                const vpcIds = accountData?.vpcIds || [];

                const newExpandedVpcs = new Set(prev.expandedVpcs);
                vpcIds.forEach((id: string) => newExpandedVpcs.delete(id));

                // Update breadcrumbs - remove this account and anything after
                const accountBreadcrumbIndex = prev.breadcrumbs.findIndex(
                    b => b.type === 'aws-account' && b.id === accountId
                );
                const newBreadcrumbs = accountBreadcrumbIndex >= 0
                    ? prev.breadcrumbs.slice(0, accountBreadcrumbIndex)
                    : prev.breadcrumbs;

                return {
                    ...prev,
                    expandedAwsAccounts: newExpandedAccounts,
                    expandedVpcs: newExpandedVpcs,
                    breadcrumbs: newBreadcrumbs.length > 0
                        ? newBreadcrumbs
                        : [{ label: 'Infrastructure', type: 'root' }]
                };
            });
        } else {
            // =====================================================
            // EXPAND AWS ACCOUNT
            // =====================================================

            // Load data if not cached
            if (loadAwsAccountData) {
                try {
                    await loadAwsAccountData(accountId, connection);
                } catch (err) {
                    console.error(`[Expansion] Failed to load AWS account data:`, err);
                    return; // Don't expand if data load failed
                }
            }

            setViewState(prev => {
                const newExpandedAccounts = new Set(prev.expandedAwsAccounts);
                newExpandedAccounts.add(accountId);

                // Add to breadcrumbs if not already there
                const hasAccountBreadcrumb = prev.breadcrumbs.some(
                    b => b.type === 'aws-account' && b.id === accountId
                );

                const accountLabel = `AWS ${accountId}`;
                const newBreadcrumbs = hasAccountBreadcrumb
                    ? prev.breadcrumbs
                    : [...prev.breadcrumbs, {
                        label: accountLabel,
                        type: 'aws-account' as const,
                        id: accountId
                    }];

                return {
                    ...prev,
                    expandedAwsAccounts: newExpandedAccounts,
                    breadcrumbs: newBreadcrumbs
                };
            });
        }
    }, [awsConnections, awsAccountCache, viewState.expandedAwsAccounts, loadAwsAccountData, startAnimation]);

    // =========================================================================
    // TOGGLE VPC EXPANSION
    // =========================================================================

    const toggleVpcExpansion = useCallback((vpcId: string, accountId: string) => {
        const isCurrentlyExpanded = viewState.expandedVpcs.has(vpcId);

        // Start animation
        startAnimation(`vpc:${vpcId}`);

        if (isCurrentlyExpanded) {
            // =====================================================
            // COLLAPSE VPC
            // =====================================================

            setViewState(prev => {
                const newExpandedVpcs = new Set(prev.expandedVpcs);
                newExpandedVpcs.delete(vpcId);

                // Don't modify breadcrumbs - VPCs are siblings, not nested
                return {
                    ...prev,
                    expandedVpcs: newExpandedVpcs
                };
            });
        } else {
            // =====================================================
            // EXPAND VPC
            // =====================================================

            // VPC data is already loaded with the account, no need to fetch

            setViewState(prev => {
                const newExpandedVpcs = new Set(prev.expandedVpcs);
                newExpandedVpcs.add(vpcId);

                // Don't modify breadcrumbs - VPCs are siblings, not nested
                return {
                    ...prev,
                    expandedVpcs: newExpandedVpcs
                };
            });
        }
    }, [viewState.expandedVpcs, startAnimation]);

    // =========================================================================
    // TOGGLE OCI TENANCY EXPANSION
    // =========================================================================

    const toggleOciTenancyExpansion = useCallback(async (tenancyOcid: string) => {
        const connection = ociConnections.find(c => c.tenancy_ocid === tenancyOcid);
        if (!connection) {
            console.warn(`[Expansion] OCI connection not found for tenancy: ${tenancyOcid}`);
            return;
        }

        const isCurrentlyExpanded = viewState.expandedOciTenancies.has(tenancyOcid);

        // Start animation
        startAnimation(`oci-tenancy:${tenancyOcid}`);

        if (isCurrentlyExpanded) {
            // =====================================================
            // COLLAPSE OCI TENANCY
            // =====================================================

            setViewState(prev => {
                const newExpandedTenancies = new Set(prev.expandedOciTenancies);
                newExpandedTenancies.delete(tenancyOcid);

                // Also collapse any expanded VCNs in this tenancy
                const tenancyData = ociTenancyCache.get(tenancyOcid);
                const newExpandedVcns = new Set(prev.expandedVcns);
                if (tenancyData) {
                    tenancyData.vcnIds.forEach((vcnId: string) => newExpandedVcns.delete(vcnId));
                }

                return {
                    ...prev,
                    expandedOciTenancies: newExpandedTenancies,
                    expandedVcns: newExpandedVcns,
                    breadcrumbs: prev.breadcrumbs.filter(b => b.type !== 'oci-tenancy' && b.type !== 'oci-vcn')
                };
            });
        } else {
            // =====================================================
            // EXPAND OCI TENANCY
            // =====================================================

            // Load tenancy data if not already loaded
            if (loadOciTenancyData && !ociTenancyCache.has(tenancyOcid)) {
                try {
                    await loadOciTenancyData(tenancyOcid, connection);
                } catch (err) {
                    console.error(`[Expansion] Failed to load OCI tenancy ${tenancyOcid}:`, err);
                    return;
                }
            }

            setViewState(prev => {
                const newExpandedTenancies = new Set(prev.expandedOciTenancies);
                newExpandedTenancies.add(tenancyOcid);

                // Add breadcrumb for tenancy
                const newBreadcrumbs = [
                    ...prev.breadcrumbs,
                    { label: connection.connection_name || tenancyOcid.substring(0, 20), type: 'oci-tenancy' as const, id: tenancyOcid }
                ];

                return {
                    ...prev,
                    expandedOciTenancies: newExpandedTenancies,
                    breadcrumbs: newBreadcrumbs
                };
            });
        }
    }, [ociConnections, ociTenancyCache, viewState.expandedOciTenancies, loadOciTenancyData, startAnimation]);

    // =========================================================================
    // TOGGLE VCN EXPANSION
    // =========================================================================

    const toggleVcnExpansion = useCallback((vcnId: string, tenancyOcid: string) => {
        const isCurrentlyExpanded = viewState.expandedVcns.has(vcnId);

        // Start animation
        startAnimation(`vcn:${vcnId}`);

        if (isCurrentlyExpanded) {
            // =====================================================
            // COLLAPSE VCN
            // =====================================================

            setViewState(prev => {
                const newExpandedVcns = new Set(prev.expandedVcns);
                newExpandedVcns.delete(vcnId);

                // Don't modify breadcrumbs - VCNs are siblings, not nested
                return {
                    ...prev,
                    expandedVcns: newExpandedVcns
                };
            });
        } else {
            // =====================================================
            // EXPAND VCN
            // =====================================================

            // VCN data is already loaded with the tenancy, no need to fetch

            setViewState(prev => {
                const newExpandedVcns = new Set(prev.expandedVcns);
                newExpandedVcns.add(vcnId);

                // Don't modify breadcrumbs - VCNs are siblings, not nested
                return {
                    ...prev,
                    expandedVcns: newExpandedVcns
                };
            });
        }
    }, [viewState.expandedVcns, startAnimation]);

    // =========================================================================
    // COLLAPSE ALL
    // =========================================================================

    const collapseAll = useCallback(() => {
        setViewState({
            expandedClusters: new Set(),
            expandedNamespaces: new Set(),
            expandedAwsAccounts: new Set(),
            expandedVpcs: new Set(),
            expandedOciTenancies: new Set(),
            expandedVcns: new Set(),
            breadcrumbs: [{ label: 'Infrastructure', type: 'root' }],
            focusedCluster: undefined
        });
    }, []);

    // =========================================================================
    // EXPAND ALL
    // =========================================================================

    const expandAllClusters = useCallback(async () => {
        // Phase 1: Load and expand all clusters, AWS accounts, and OCI tenancies in parallel
        const clusterPromises = clusters.map(cluster =>
            loadClusterData(cluster.uid, cluster).catch(err => {
                console.warn(`[Expansion] Failed to load cluster ${cluster.name}:`, err);
                return null;
            })
        );

        const awsPromises = awsConnections.map(connection =>
            loadAwsAccountData?.(connection.account_id, connection).catch(err => {
                console.warn(`[Expansion] Failed to load AWS account ${connection.account_id}:`, err);
                return null;
            }) ?? Promise.resolve(null)
        );

        const ociPromises = ociConnections.map(connection =>
            loadOciTenancyData?.(connection.tenancy_ocid, connection).catch(err => {
                console.warn(`[Expansion] Failed to load OCI tenancy ${connection.tenancy_ocid}:`, err);
                return null;
            }) ?? Promise.resolve(null)
        );

        const [clusterResults, awsResults, ociResults] = await Promise.all([
            Promise.all(clusterPromises),
            Promise.all(awsPromises),
            Promise.all(ociPromises)
        ]);

        // Phase 2: Load all namespaces from expanded clusters
        const allNamespaceIds: string[] = [];
        const namespaceLoadPromises: Promise<NamespaceCacheEntry | null>[] = [];

        clusterResults.forEach((clusterData, index) => {
            if (!clusterData) return;
            const cluster = clusters[index];

            clusterData.namespaces?.forEach(namespace => {
                allNamespaceIds.push(namespace.id);
                namespaceLoadPromises.push(
                    loadNamespaceData(namespace.id, namespace, cluster.uid).catch(err => {
                        console.warn(`[Expansion] Failed to load namespace ${namespace.name}:`, err);
                        return null;
                    })
                );
            });
        });

        // Load namespaces in parallel (batch to avoid overwhelming the server)
        await Promise.all(namespaceLoadPromises);

        // Collect all VPC IDs from loaded AWS accounts
        const allVpcIds: string[] = [];
        awsResults.forEach(accountData => {
            if (accountData?.vpcIds) {
                allVpcIds.push(...accountData.vpcIds);
            }
        });

        // Collect all VCN IDs from loaded OCI tenancies
        const allVcnIds: string[] = [];
        ociResults.forEach(tenancyData => {
            if (tenancyData?.vcnIds) {
                allVcnIds.push(...tenancyData.vcnIds);
            }
        });

        // Update state with everything expanded
        setViewState(prev => ({
            ...prev,
            expandedClusters: new Set(clusters.map(c => c.uid)),
            expandedNamespaces: new Set(allNamespaceIds),
            expandedAwsAccounts: new Set(awsConnections.map(c => c.account_id)),
            expandedVpcs: new Set(allVpcIds),
            expandedOciTenancies: new Set(ociConnections.map(c => c.tenancy_ocid)),
            expandedVcns: new Set(allVcnIds),
            breadcrumbs: [{ label: 'Infrastructure', type: 'root' }]
        }));
    }, [clusters, awsConnections, ociConnections, loadClusterData, loadNamespaceData, loadAwsAccountData, loadOciTenancyData]);

    // =========================================================================
    // HANDLE NODE CLICK
    // =========================================================================

    const handleNodeClick = useCallback(async (
        event: React.MouseEvent | null,
        node: Node<CustomNodeData>
    ) => {
        if (!node.data) return;

        const { type, clusterUID, namespaceID, parentClusterUID, isExpandable, awsAccountId, vpcId, ociTenancyOcid, vcnId, parentOciTenancyOcid, cloudResource } = node.data;

        // Handle cloud resource leaf nodes (open detail dialog)
        if ((type === 'aws-resource' || type === 'oci-resource') && cloudResource && onCloudResourceClick) {
            onCloudResourceClick(cloudResource);
            return;
        }

        // Only handle expandable nodes for the rest
        if (!isExpandable) {
            return;
        }

        if (type === 'cluster' && clusterUID) {
            await toggleClusterExpansion(clusterUID);
        } else if (type === 'namespace' && namespaceID && parentClusterUID) {
            await toggleNamespaceExpansion(namespaceID, parentClusterUID);
        } else if (type === 'aws-account' && awsAccountId) {
            await toggleAwsAccountExpansion(awsAccountId);
        } else if (type === 'aws-vpc' && vpcId && awsAccountId) {
            toggleVpcExpansion(vpcId, awsAccountId);
        } else if (type === 'aws-global-group' && awsAccountId) {
            // Global group is always shown when account is expanded, no toggle needed
        } else if (type === 'oci-tenancy' && ociTenancyOcid) {
            await toggleOciTenancyExpansion(ociTenancyOcid);
        } else if (type === 'oci-vcn' && vcnId && (ociTenancyOcid || parentOciTenancyOcid)) {
            toggleVcnExpansion(vcnId, ociTenancyOcid || parentOciTenancyOcid!);
        } else if (type === 'oci-global-group' && (ociTenancyOcid || parentOciTenancyOcid)) {
            // Global group toggle - same as VCN
            const tenancy = ociTenancyOcid || parentOciTenancyOcid!;
            toggleVcnExpansion('global', tenancy);
        } else if (type === 'container') {
            // Container nodes represent expanded clusters/namespaces/accounts/vpcs/tenancies/vcns
            // Clicking the header should collapse
            const containerType = node.data.containerType;
            if (containerType === 'cluster' && clusterUID) {
                await toggleClusterExpansion(clusterUID);
            } else if (containerType === 'namespace' && namespaceID && parentClusterUID) {
                await toggleNamespaceExpansion(namespaceID, parentClusterUID);
            } else if (containerType === 'aws-account' && awsAccountId) {
                await toggleAwsAccountExpansion(awsAccountId);
            } else if (containerType === 'aws-vpc' && vpcId && awsAccountId) {
                toggleVpcExpansion(vpcId, awsAccountId);
            } else if (containerType === 'oci-tenancy' && ociTenancyOcid) {
                await toggleOciTenancyExpansion(ociTenancyOcid);
            } else if (containerType === 'oci-vcn' && vcnId && (ociTenancyOcid || parentOciTenancyOcid)) {
                toggleVcnExpansion(vcnId, ociTenancyOcid || parentOciTenancyOcid!);
            }
        }
    }, [toggleClusterExpansion, toggleNamespaceExpansion, toggleAwsAccountExpansion, toggleVpcExpansion, toggleOciTenancyExpansion, toggleVcnExpansion, onCloudResourceClick]);

    // =========================================================================
    // HANDLE BREADCRUMB CLICK
    // =========================================================================

    const handleBreadcrumbClick = useCallback((index: number) => {
        const crumb = viewState.breadcrumbs[index];

        if (crumb.type === 'root') {
            // Go to root - collapse everything
            collapseAll();
        } else if (crumb.type === 'cluster') {
            // Collapse all namespaces within this cluster
            setViewState(prev => ({
                ...prev,
                expandedNamespaces: new Set(),
                breadcrumbs: prev.breadcrumbs.slice(0, index + 1)
            }));
        } else if (crumb.type === 'aws-account') {
            // Collapse all VPCs within this account
            setViewState(prev => ({
                ...prev,
                expandedVpcs: new Set(),
                breadcrumbs: prev.breadcrumbs.slice(0, index + 1)
            }));
        } else if (crumb.type === 'oci-tenancy') {
            // Collapse all VCNs within this tenancy
            setViewState(prev => ({
                ...prev,
                expandedVcns: new Set(),
                breadcrumbs: prev.breadcrumbs.slice(0, index + 1)
            }));
        }
        // Note: 'namespace', 'aws-vpc', and 'oci-vcn' types are no longer added to breadcrumbs
        // since they are siblings (can have multiple expanded at once), not a nested hierarchy
    }, [viewState.breadcrumbs, collapseAll]);

    // =========================================================================
    // RETURN
    // =========================================================================

    return {
        viewState,
        toggleClusterExpansion,
        toggleNamespaceExpansion,
        toggleAwsAccountExpansion,
        toggleVpcExpansion,
        toggleOciTenancyExpansion,
        toggleVcnExpansion,
        collapseAll,
        expandAllClusters,
        handleNodeClick,
        handleBreadcrumbClick,
        isClusterExpanded,
        isNamespaceExpanded,
        isAwsAccountExpanded,
        isVpcExpanded,
        isOciTenancyExpanded,
        isVcnExpanded,
        animatingNodes
    };
}
